// Builds the payload handed to the generate-recipe edge function.
//
// The edge function used to see only { name, canonicalId, amount, unit,
// category } per pantry item. That meant Claude couldn't prioritize
// food about to expire, didn't know the user's dietary constraints or
// cooking level, couldn't reach for non-obvious pairings suggested by
// canonical enrichment, and couldn't learn from what the user had
// actually cooked before.
//
// This helper assembles a curated-rich payload. "Curated" rather than
// firehose because:
//
//   - 200K context is fine on Haiku 4.5 but signal dilution is real;
//     a focused prompt produces sharper drafts than a maximal one.
//   - Every field shipped is a new privacy surface. We send only what
//     moves the recipe-combination needle.
//   - More context anchors the model; we still want REGEN variety. The
//     "lean" mode is for REGEN calls so the second draft isn't pinned
//     to the same flavor-profile pairings.
//
// Sanitization policy: every user-sourced string is stripped of
// control characters and backtick-fence markers before entering the
// payload. The edge function's prompt still trusts the shape, but
// the strings themselves can't smuggle instructions.

import {
  filterPantryByCourse,
  courseHasPantryFilter,
  coursePaletteScore,
} from "./courseCompat";
import { findIngredient, tierForCanonical, inferCanonicalFromNameLongestMatch } from "../data/ingredients";
import { allergenExclusionStrings } from "../data/allergens";

const MAX_PANTRY_ITEMS   = 40;   // keep the prompt bounded
const EXPIRING_SOON_DAYS = 7;    // "about to spoil" threshold
const COOK_HISTORY_LIMIT = 20;   // recent cooks summarized
const DISTINCTIVE_SLOTS  = 6;    // reserved for character ingredients

// Flavor-maker net for pantries whose enrichment hasn't landed yet —
// slugs that are distinctive by identity. Coarse on purpose; the
// flavor_profile punch test above is the primary signal.
const DISTINCTIVE_SLUG_RE = /(miso|gochujang|gochugaru|harissa|kimchi|sauerkraut|preserved_lemon|anchov|fish_sauce|oyster_sauce|tahini|za_atar|sumac|chile|chili|chipotle|calabrian|szechuan|sichuan|curry_paste|doenjang|sambal|capers|olives?|feta|parmesan|pecorino|gjetost|brunost|blue_cheese|goat_cheese|horseradish|wasabi|mole|dukkah|ras_el_hanout|garam_masala|furikake|nori|bonito|dashi|lemongrass|galangal|kaffir|saffron|cardamom|star_anise|fennel_seed|smoked_paprika|nduja|chorizo|pancetta|guanciale|prosciutto)/;

/**
 * @param {object} args
 * @param {Array}  args.pantry             — rows from usePantry
 * @param {object} [args.profile]          — row from useProfile
 * @param {object} [args.ingredientInfo]   — { getInfo(canonicalId) → info | null }
 * @param {Array}  [args.cookLogs]         — rows from useCookLog (viewer's cooks)
 * @param {"rich" | "lean"} [args.mode]    — "lean" strips enrichment/profile/history
 *                                           so REGEN doesn't re-anchor on the same
 *                                           pairings as the first draft.
 * @param {string[]} [args.starIngredientIds] — canonical slugs the user has
 *                                           explicitly chosen to build around
 *                                           (from the STAR INGREDIENTS picker).
 *                                           Boosted to the top of the ranked
 *                                           pantry + stamped star:true so the
 *                                           prompt can reference them.
 * @returns {{
 *   pantry:          Array,           // capped, sanitized, star-first then expiring-first
 *   pantryFiltered:  boolean,         // true when course+priority actually trimmed rows
 *   context:         object | null,   // { profile, history } or null in lean mode
 * }}
 */
export function buildAIContext({
  pantry = [],
  profile,
  ingredientInfo,
  cookLogs = [],
  mode = "rich",
  starIngredientIds = [],
  // Classic-priority pantry filter. When the user picks a tight
  // course (bake / dessert / prep) AND priority === "classic", we
  // pre-filter the pantry rows Claude sees to items compatible with
  // that category. Claude physically can't draft a hot-dog skillet
  // under Baked Goods when hot dogs aren't in the palette. Pass-
  // through (no filter) for course="any" / main / side / appetizer,
  // for priority="chefs_choice" (chef wants free hand), and for
  // priority="pantry" (every pantry row is part of the source list,
  // hiding compatibility-incompatible rows would mislead the user).
  course,
  priority,
} = {}) {
  const rich = mode === "rich";
  const now = Date.now();
  // Pre-filter BEFORE the ranking pass so starred incompatible items
  // don't get hoisted to the top only to produce narrative pressure
  // for Claude to respect them. pantryFiltered lets the edge fn's
  // PRECEDENCE block say the truth about what Claude is looking at
  // (historically it claimed the pantry was filtered even when no
  // filter had run — Claude then wove expiring savory items into
  // bakes and main-course drafts ignored the category entirely).
  const pantryFiltered = courseHasPantryFilter(course, priority);
  pantry = filterPantryByCourse(pantry, course, priority);
  // Leftover meals (kind='meal' with sourceRecipeSlug) belong to the
  // separate "eat the leftover" flow (IAteThisSheet / Plan's leftover
  // shortcut), NOT to "what should I cook tonight?". Sending them in
  // the pantry made Claude treat them as a recipe target — a leftover
  // broccoli cheddar soup got drafted as "reheat in a bread bowl
  // with salami and pickles" instead of a fresh dish using the
  // user's actual ingredients. Strip them here so the sketch model
  // never sees them.
  // A finished leftover dish is NOT an ingredient — the model must never
  // pull "Leftover Classic Vegetarian Alfredo" into a fresh recipe. The
  // old gate only caught kind === "meal", but legacy / differently-stamped
  // leftover rows (kind null, sourceRecipeSlug set, or just named
  // "Leftover X") slipped through and landed as recipe ingredients. Catch
  // all three signals.
  pantry = pantry.filter((p) => {
    if (p?.kind === "meal" || p?.kind === "leftovers") return false;
    if (p?.sourceRecipeSlug || p?.source_recipe_slug || p?.from_pantry_row_id) return false;
    const nm = typeof p?.name === "string" ? p.name.trim() : "";
    if (/^leftover[\s:-]/i.test(nm)) return false;
    return true;
  });
  const getInfo = rich && ingredientInfo?.getInfo
    ? (id) => ingredientInfo.getInfo(id)
    : () => null;

  // Set of canonical identifiers the user actually has in their
  // pantry. Used below to filter `enrichment.pairs` so the prompt
  // only mentions companion ingredients the user could legitimately
  // reach for. Without this gate, a stale `pairs` entry on a single
  // ingredient_info row (e.g. butter → ["miso paste", "brown butter"])
  // gets injected into EVERY recipe involving that ingredient — the
  // AI reads the suggestion as license to add miso to a steak rub
  // even when the user has no miso. A proper "pairs" hint should be
  // a cross-pantry combinator, not a shopping-list inflator.
  const pantryCanonicals = new Set();
  for (const p of pantry) {
    if (p?.canonicalId) pantryCanonicals.add(p.canonicalId);
    if (p?.ingredientId) pantryCanonicals.add(p.ingredientId);
  }

  const starSet = new Set(
    Array.isArray(starIngredientIds) ? starIngredientIds.filter(Boolean) : []
  );

  // Rank pantry by SCORE, not by recency. Recently-added items used to
  // bubble to the top of the truncated 40 — instant yeast scanned this
  // morning would land in pasta drafts purely on freshness. The score
  // formula below uses metadata the canonical row already carries
  // (hub / category / tier / pair coverage with the rest of the
  // pantry) plus a course-palette nudge so the right ingredients
  // surface for what the user is actually trying to cook.
  //
  // Score components (rough weights):
  //   +1000   isStar — explicit user ask, hard top
  //   + 300   expiring-within-window  (− daysToExpiry sub-rank)
  //   + 100   tier-1 primitive (workhorses: butter, flour, salt, etc.)
  //   ± 0-80  course-palette boost/demote (see courseCompat.js)
  //   + 8 × pair-coverage with rest of pantry (rich mode only)
  //   + jitter(0..15) — small randomness so REGEN doesn't pin to
  //                     identical pantry slice as the first draft
  const ranked = [...pantry]
    .map(p => {
      const canonicalId = p.canonicalId || p.ingredientId || null;
      const ingredient  = canonicalId ? findIngredient(canonicalId) : null;

      const isStar = starSet.size > 0 && (
        starSet.has(p.canonicalId) || starSet.has(p.ingredientId)
      );
      const daysToExpiry = daysUntil(p.expiresAt, now);
      const expiringSoon = daysToExpiry != null &&
                           daysToExpiry <= EXPIRING_SOON_DAYS;

      // Pair-coverage: how many of this canonical's `pairs` are also
      // in the pantry? More = better recipe anchor against the rest
      // of the shelf. Lean mode skips enrichment so coverage = 0 there
      // (intentional; lean is REGEN-mode and we want different output).
      const info = canonicalId ? getInfo(canonicalId) : null;
      let pairCoverage = 0;
      const pairs = Array.isArray(info?.pairs) ? info.pairs : [];
      for (const pair of pairs) {
        if (typeof pair !== "string") continue;
        const slug = pair.toLowerCase().replace(/\s+/g, "_");
        if (pantryCanonicals.has(pair) || pantryCanonicals.has(slug)) {
          pairCoverage++;
        }
      }

      const tier    = tierForCanonical(canonicalId);
      const palette = coursePaletteScore({ canonicalId, ingredient, course });

      // Distinctive = strong flavor identity that ISN'T a tier-1
      // workhorse — the ingredients that make a draft interesting
      // (gochujang, preserved lemon, anchovies, brunost). The tier-1
      // boost above systematically crowds these out of the truncated
      // slice, so the model always saw butter/flour/salt and never
      // the character shelf. Signals: enrichment taste punch, or a
      // known flavor-maker token in the slug.
      const fp = info?.flavor_profile || null;
      const punch = fp && (
        (fp.heat ?? 0) >= 2 || (fp.umami ?? 0) >= 4 ||
        (fp.sour ?? 0) >= 4 || (fp.aromatic_intensity ?? 0) >= 4
      );
      const distinctive = tier !== 1 && !!(
        punch || DISTINCTIVE_SLUG_RE.test(String(canonicalId || ""))
      );

      let score = 0;
      if (isStar)        score += 1000;
      if (expiringSoon)  score += 300 - (daysToExpiry ?? 0);
      if (tier === 1)    score += 100;
      score += palette;
      score += pairCoverage * 8;
      score += Math.random() * 15;

      return { item: p, isStar, daysToExpiry, expiringSoon, score, distinctive };
    })
    .sort((a, b) => b.score - a.score);

  // Reserved distinctive slots: when the pantry overflows the cap,
  // guarantee up to N character ingredients survive the cut by
  // displacing the lowest-scored non-distinctive rows. No-op when
  // the head already carries enough distinctive items (or the
  // pantry has none).
  let rankedSlice;
  if (ranked.length <= MAX_PANTRY_ITEMS) {
    rankedSlice = ranked;
  } else {
    const head = ranked.slice(0, MAX_PANTRY_ITEMS);
    const inHead = head.filter((r) => r.distinctive).length;
    const missing = Math.max(0, DISTINCTIVE_SLOTS - inHead);
    const promote = missing > 0
      ? ranked.slice(MAX_PANTRY_ITEMS).filter((r) => r.distinctive).slice(0, missing)
      : [];
    if (promote.length === 0) {
      rankedSlice = head;
    } else {
      // Drop from the tail of the head, skipping stars + expiring +
      // already-distinctive rows — character promotion must never
      // silently evict something the user starred or is about to lose.
      const keep = [...head];
      let toDrop = promote.length;
      for (let i = keep.length - 1; i >= 0 && toDrop > 0; i--) {
        if (keep[i].isStar || keep[i].distinctive || keep[i].expiringSoon) continue;
        keep.splice(i, 1);
        toDrop--;
      }
      rankedSlice = [...keep, ...promote].slice(0, MAX_PANTRY_ITEMS);
    }
  }

  // Shape per-item — always send the basic identity axes plus state /
  // expiresAt / location / ingredients[]. In rich mode we add the
  // enrichment triplet (flavorProfile / pairs / diet) because those
  // three fields are what actually drive "what goes with what."
  const pantryOut = rankedSlice.map(({ item, daysToExpiry, isStar }) => {
    const canonicalId = item.canonicalId || item.ingredientId || null;
    // Identity is `canonicalId` (the parent — what this thing IS).
    // `ingredients` are CHILDREN of the parent canonical: raw
    // simplest-form ingredients composing the row (frank →
    // ['cheese']; mac_and_cheese → ['macaroni','cheese']). Children
    // inherit properties up to the parent (a frank with cheese
    // child is dairy-tagged for diet checks) but they are NOT peer
    // identities — never match "cheese" recipe slots against a frank
    // row at primary identity tier.
    //
    // Display name (`name`) is sent ONLY when canonicalId is null —
    // it's a fallback so Claude can reason about a free-text /
    // legacy / unlinked row instead of seeing it as "(unlinked)" and
    // ignoring it. When canonical IS set, name stays out: it's a
    // derived display string that creates a competing label the
    // model would have to reconcile ("is 'Mission Tortillas' the
    // same as id:tortillas?"). Empty pantry payloads were happening
    // because users with mostly-unlinked legacy rows would render as
    // 122× "(unlinked)" in the prompt — Claude treated that as zero
    // signal and drafted from staples.
    // Strip the user's brand off the free-text name before it reaches
    // the LLM. Without this, an unlinked row with name="Truff Hotter
    // Sauce" + brand="Truff" sends "Truff Hotter Sauce" to Claude and
    // Claude dutifully writes "Truff" verbatim into the recipe output —
    // brand belongs to the pairing layer, NEVER the generator. The
    // edge fn already filters the `brand` field out of the prompt; this
    // closes the second leak through the unlinked-name fallback.
    const stripBrand = (name, brand) => {
      if (!name || !brand) return name || null;
      const trimmed = String(name).trim();
      const lower = trimmed.toLowerCase();
      const brandLower = String(brand).toLowerCase();
      if (lower.startsWith(brandLower)) {
        return trimmed.substring(brand.length).replace(/^[\s,'\-]+/, "").trim() || null;
      }
      return trimmed || null;
    };
    // TRUFF-CLASS FIX: before shipping ANY free text, try to resolve
    // the unlinked name through the canonical resolver. A row named
    // "Truff Hotter Sauce" with NO brand field and NO canonical
    // sailed past stripBrand verbatim, and Claude wrote it into a
    // teriyaki as the sauce base (½ cup of truffle hot sauce). If
    // the alias map binds the name (→ hot_sauce), the model sees the
    // canonical id like every linked row; only genuinely
    // unresolvable names ship as brand-stripped text.
    let effectiveCanonicalId = canonicalId;
    let fallbackName = null;
    if (!effectiveCanonicalId) {
      const cleaned = stripBrand(item.name, item.brand);
      const inferred = cleaned ? inferCanonicalFromNameLongestMatch(cleaned) : null;
      if (inferred) {
        effectiveCanonicalId = inferred;
      } else {
        fallbackName = safeStr(cleaned).slice(0, 80) || null;
      }
    }
    const out = {
      canonicalId: effectiveCanonicalId,
      ...(effectiveCanonicalId ? {} : { name: fallbackName }),
      ingredients:   Array.isArray(item.ingredients) ? item.ingredients : [],
      amount:        item.amount ?? null,
      unit:          item.unit ?? null,
      category:      item.category ?? null,
      // Brand (migration 0061) — manufacturer label parsed off the raw
      // name. Kept as its own axis so the AI can distinguish
      // "Great Value Butter" from "Kerrygold Butter" when dietary
      // claims differ, without polluting the canonical identity.
      brand:         item.brand ?? null,
      state:         item.state ?? null,
      // Cut axis (migration 0122). "breast" / "thigh" / "ribeye" /
      // etc. Surfaced so the AI knows anatomy without subbing away.
      cut:           item.cut ?? null,
      location:      item.location ?? null,
      daysToExpiry:  daysToExpiry,            // negative = already expired
      kind:          item.kind ?? null,       // "ingredient" | "leftovers" | "compound"
      // Flag for the prompt — "user explicitly wants this in the
      // recipe." Prompt rules treat starred rows as the anchor
      // ingredient, overriding the expiring-soon default.
      ...(isStar ? { star: true } : {}),
    };
    if (rich) {
      // effectiveCanonicalId so resolver-bound unlinked rows (the
      // Truff-class fix above) get enrichment like any linked row.
      const info = getInfo(effectiveCanonicalId);
      if (info) {
        // Pull the enrichment fields that directly shape recipe prose.
        // Nutrition / storage / skillDev / cultural notes are still
        // skipped — broad signal, low per-item value for drafting.
        //
        // `pairs` is filtered to in-pantry only (see pantryCanonicals
        // above). The original raw list could include suggestions for
        // companion ingredients the user doesn't have — once those
        // hit the prompt, the AI treats them as available, e.g.
        // "Pairs: miso paste, brown butter" injects miso/brown-butter
        // into recipes regardless of what the user actually owns.
        // The filter ensures pairs only signal "use what you have
        // alongside this," never "shop for what you don't."
        //
        // `technique` is the per-ingredient hand-curated technique map
        // (caramelize=35-40 min, sauté=5-7 min, ...). Only present for
        // ~50 staple canonicals today. Final-pass-only consumer because
        // it's prose-shaping; sketch doesn't need it.
        const rawPairs = Array.isArray(info.pairs) ? info.pairs : [];
        const filteredPairs = rawPairs.filter(p => {
          if (typeof p !== "string") return false;
          // pairs entries are usually canonical ids (e.g. "garlic").
          // Some legacy entries are display names (e.g. "Brown Butter").
          // Match either by canonicalId set OR by slugified-name set.
          const slug = p.toLowerCase().replace(/\s+/g, "_");
          return pantryCanonicals.has(p) || pantryCanonicals.has(slug);
        }).slice(0, 12);
        out.enrichment = {
          flavorProfile: safeStr(info.flavorProfile).slice(0, 200) || null,
          pairs:         filteredPairs,
          diet:          info.diet || null,
          technique:     info.technique || null,
        };
        // Compact taste-signal additions (enrich-ingredient v6 fields
        // + bundled flavor.heatChange). All optional — keys are OMITTED
        // entirely when the layer hasn't been enriched, so the edge fn
        // renders nothing for them. Budget: ~1-2 rendered lines/item.
        //
        // taste — 0-5 numeric scales (salty/sweet/sour/bitter/umami/
        // heat/fat/aromatic_intensity). Only finite values; zeros
        // dropped (a zero carries no drafting signal, only tokens).
        const fp = info.flavor_profile;
        if (fp && typeof fp === "object") {
          const taste = {};
          for (const k of ["salty", "sweet", "sour", "bitter", "umami", "heat", "fat", "aromatic_intensity"]) {
            const v = Number(fp[k]);
            if (Number.isFinite(v) && v > 0) taste[k] = v;
          }
          if (Object.keys(taste).length) out.enrichment.taste = taste;
        }
        // aromatic — the ingredient's aromatic family (allium /
        // citrus / fermented / …). "none" is dropped like a zero.
        if (typeof info.aromatic_category === "string") {
          const aromatic = safeStr(info.aromatic_category).slice(0, 40);
          if (aromatic && aromatic !== "none") out.enrichment.aromatic = aromatic;
        }
        // heatChange — raw/cooked/charred flavor descriptors from the
        // bundled-style flavor object. Sliced hard: these are prose.
        const hc = info.flavor && typeof info.flavor === "object" ? info.flavor.heatChange : null;
        if (hc && typeof hc === "object") {
          const heatChange = {};
          for (const k of ["raw", "cooked", "charred"]) {
            const v = typeof hc[k] === "string" ? safeStr(hc[k]).slice(0, 80) : "";
            if (v) heatChange[k] = v;
          }
          if (Object.keys(heatChange).length) out.enrichment.heatChange = heatChange;
        }
        // acidity — level bucket only (alkaline/neutral/low/medium/
        // high); pH + notes stay client-side.
        if (info.acidity && typeof info.acidity === "object" && typeof info.acidity.level === "string") {
          const acidity = safeStr(info.acidity.level).slice(0, 20);
          if (acidity) out.enrichment.acidity = acidity;
        }
      }
    }
    return out;
  });

  if (!rich) {
    // LEAN mode exists to keep regen prompts cheap — but the
    // SAFETY-CRITICAL profile lines must survive EVERY mode.
    // Returning context:null here meant every reroll's sketch
    // rendered without the "ALLERGEN-FREE — ABSOLUTE" block and
    // without dietary constraints: a shellfish-allergic user's
    // reroll could propose shrimp, and a dairy-free regen shipped
    // with a cup of parmesan. History/skills/enrichment stay
    // dropped; allergens and diet never do.
    const leanProfile = profile ? {
      dietary:    safeStr(profile.dietary) || null,
      veganStyle: safeStr(profile.vegan_style) || null,
      allergenExclusions: allergenExclusionStrings(profile.allergies),
    } : null;
    return {
      pantry: pantryOut,
      pantryFiltered,
      context: leanProfile ? { profile: leanProfile } : null,
    };
  }

  // Cook-history summary — bucket ratings, surface the top cuisines +
  // titles the user has leaned into. Never send raw notes (free-text
  // is the highest prompt-injection surface and low signal for
  // drafting).
  const history = summarizeCooks(cookLogs.slice(0, COOK_HISTORY_LIMIT));

  // Profile slice — only the fields that shape the draft. Skip xp,
  // streak, cuisines_cooked (rolled up in history), name.
  const profileSlice = profile ? {
    dietary:    safeStr(profile.dietary)   || null,
    veganStyle: safeStr(profile.vegan_style) || null,
    level:      safeStr(profile.skill_self_report) || null,
    goal:       safeStr(profile.goal)      || null,
    // SAFETY-CRITICAL: human-readable allergen exclusion lines the edge fn
    // renders as an ABSOLUTE "never include" block. Derived from
    // profile.allergies via the allergens taxonomy. Empty when the user has
    // no allergies OR has toggled the guard off for this draft (AIRecipe
    // passes profile.allergies: [] in that case — see allergyGuardOff).
    allergenExclusions: allergenExclusionStrings(profile.allergies),
    // Top skills by level, 3 at most. Surfaces what the user is
    // actively practicing so the draft picks appropriately-difficult
    // techniques.
    topSkills: topSkills(profile.skill_levels, 3),
  } : null;

  return {
    pantry: pantryOut,
    pantryFiltered,
    context: {
      profile: profileSlice,
      history,
    },
  };
}

// ───────────────────────────────────────────────────────────────────

function daysUntil(when, nowMs) {
  if (!when) return null;
  const t = +new Date(when);
  if (!Number.isFinite(t)) return null;
  return Math.round((t - nowMs) / (24 * 60 * 60 * 1000));
}

// Strip control chars + code-fence markers from user-entered strings
// so they can't smuggle prompt instructions into the edge function.
// Conservative: we drop `, not escape it, since nothing legitimate in
// an ingredient name uses a backtick.
function safeStr(s) {
  if (typeof s !== "string") return "";
  return s
    .replace(/[\u0000-\u001f\u007f]+/g, " ")  // control chars
    .replace(/`+/g, "")                        // code-fence markers
    .trim();
}

function topSkills(levels, n) {
  if (!levels || typeof levels !== "object") return [];
  return Object.entries(levels)
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, n)
    .map(([id, v]) => ({ id, level: Number(v) }));
}

function summarizeCooks(logs) {
  if (!logs.length) return { cookCount: 0 };
  const ratingCounts = { nailed: 0, good: 0, meh: 0, rough: 0, unrated: 0 };
  const cuisineCounts = {};
  // "nailed" is the proxy for "loved it" — cook_log_favorites is a
  // separate join (per-viewer favorites) that we'd have to fetch
  // independently. Rating=nailed is captured on the log row itself
  // so it comes for free. Good enough as a "what does the user
  // cook well and return to" signal.
  const nailedTitles = [];

  for (const log of logs) {
    const r = log.rating || null;
    if (r && ratingCounts[r] != null) ratingCounts[r]++;
    else ratingCounts.unrated++;
    const c = safeStr(log.recipeCuisine || log.cuisine || "").toLowerCase();
    if (c) cuisineCounts[c] = (cuisineCounts[c] || 0) + 1;
    if (r === "nailed" && log.recipeTitle) {
      nailedTitles.push(safeStr(log.recipeTitle));
    }
  }
  const topCuisines = Object.entries(cuisineCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([id, count]) => ({ id, count }));

  // Dedup titles so a user who nailed the same recipe 3 times doesn't
  // waste prompt tokens on the repeat.
  const uniqNailed = [...new Set(nailedTitles)].slice(0, 3);

  return {
    cookCount: logs.length,
    ratingCounts,
    topCuisines,
    topFavoritedTitles: uniqNailed,
  };
}
